import { useEffect, useRef, useCallback } from "react";

declare global {
  interface Window {
    kakao: any;
    __kakaoMapLoading?: boolean;
  }
}

const KAKAO_KEY = import.meta.env.VITE_KAKAO_MAP_KEY;

interface UseKakaoMapOptions {
  containerId: string;
  onPinChange: (lat: number, lng: number) => void;
  initialCenter?: { lat: number; lng: number };
  level?: number;
}

/**
 * 카카오맵 SDK 로딩 + 맵 초기화 + 핀 찍기/드래그 + 주소 검색 + 블라인드 좌표 계산
 * GenomicAI PartnerModal.tsx 검증 패턴 기반
 */
export function useKakaoMap({
  containerId,
  onPinChange,
  initialCenter = { lat: 37.5665, lng: 126.978 },
  level = 4,
}: UseKakaoMapOptions) {
  const mapRef = useRef<any>(null);
  const markerRef = useRef<any>(null);
  const onPinChangeRef = useRef(onPinChange);
  onPinChangeRef.current = onPinChange;

  // SDK 로딩 + 맵 초기화
  useEffect(() => {
    if (!KAKAO_KEY) {
      console.warn("VITE_KAKAO_MAP_KEY is not set");
      return;
    }

    function initMap() {
      const kakao = window.kakao;
      const container = document.getElementById(containerId);
      if (!container || !kakao?.maps) return;

      const center = new kakao.maps.LatLng(
        initialCenter.lat,
        initialCenter.lng,
      );
      const map = new kakao.maps.Map(container, { center, level });
      mapRef.current = map;

      // 마커 생성 (드래그 가능)
      const marker = new kakao.maps.Marker({
        position: center,
        map,
        draggable: true,
      });
      markerRef.current = marker;
      marker.setVisible(false);

      // 클릭 → 핀 이동
      kakao.maps.event.addListener(map, "click", (e: any) => {
        const pos = e.latLng;
        marker.setPosition(pos);
        marker.setVisible(true);
        onPinChangeRef.current(pos.getLat(), pos.getLng());
      });

      // 드래그 끝 → 좌표 업데이트
      kakao.maps.event.addListener(marker, "dragend", () => {
        const pos = marker.getPosition();
        onPinChangeRef.current(pos.getLat(), pos.getLng());
      });
    }

    // 1) 이미 로드됨
    if (window.kakao?.maps?.LatLng) {
      initMap();
      return;
    }

    // 2) 로딩 중 → 대기
    if (window.__kakaoMapLoading) {
      const waitId = setInterval(() => {
        if (window.kakao?.maps?.LatLng) {
          clearInterval(waitId);
          initMap();
        }
      }, 200);
      return () => clearInterval(waitId);
    }

    // 3) 새로 로딩
    window.__kakaoMapLoading = true;
    const script = document.createElement("script");
    script.src = `https://dapi.kakao.com/v2/maps/sdk.js?appkey=${KAKAO_KEY}&autoload=false&libraries=services`;
    script.onload = () => {
      window.kakao.maps.load(() => {
        window.__kakaoMapLoading = false;
        initMap();
      });
    };
    document.head.appendChild(script);
  }, [containerId]);

  // 주소 키워드 검색 → 맵 이동 + 핀
  const searchAddress = useCallback((query: string) => {
    if (!query.trim() || !window.kakao?.maps?.services) return;

    const places = new window.kakao.maps.services.Places();
    places.keywordSearch(query, (results: any[], status: any) => {
      if (status !== window.kakao.maps.services.Status.OK || !results[0])
        return;

      const first = results[0];
      const lat = parseFloat(first.y);
      const lng = parseFloat(first.x);
      const pos = new window.kakao.maps.LatLng(lat, lng);

      if (mapRef.current) {
        mapRef.current.setCenter(pos);
        mapRef.current.setLevel(3);
      }
      if (markerRef.current) {
        markerRef.current.setPosition(pos);
        markerRef.current.setVisible(true);
      }
      onPinChangeRef.current(lat, lng);
    });
  }, []);

  // 읍면동 중심 좌표 계산 (블라인드 좌표)
  const calcBlindCoords = useCallback(
    (
      sido: string,
      sigungu: string,
      eupmyeondong: string,
    ): Promise<{ lat: number; lng: number } | null> => {
      return new Promise((resolve) => {
        if (!window.kakao?.maps?.services) {
          resolve(null);
          return;
        }

        const geocoder = new window.kakao.maps.services.Geocoder();
        const query = `${sido} ${sigungu} ${eupmyeondong}`.trim();

        if (!query) {
          resolve(null);
          return;
        }

        geocoder.addressSearch(query, (results: any[], status: any) => {
          if (status === window.kakao.maps.services.Status.OK && results[0]) {
            resolve({
              lat: Math.round(parseFloat(results[0].y) * 10000) / 10000,
              lng: Math.round(parseFloat(results[0].x) * 10000) / 10000,
            });
          } else if (eupmyeondong) {
            // 읍면동 실패 시 시군구 레벨로 폴백
            const fallback = `${sido} ${sigungu}`.trim();
            geocoder.addressSearch(
              fallback,
              (results2: any[], status2: any) => {
                if (
                  status2 === window.kakao.maps.services.Status.OK &&
                  results2[0]
                ) {
                  resolve({
                    lat:
                      Math.round(parseFloat(results2[0].y) * 10000) / 10000,
                    lng:
                      Math.round(parseFloat(results2[0].x) * 10000) / 10000,
                  });
                } else {
                  resolve(null);
                }
              },
            );
          } else {
            resolve(null);
          }
        });
      });
    },
    [],
  );

  return { searchAddress, calcBlindCoords };
}
